import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsBuilding, BsKanban, BsCurrencyEuro } from 'react-icons/bs';

import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import Tabs from '#globalComponents/Tabs/Tabs';
import { useClampPage } from '#globalHooks/useClampPage';

import { useBulkRestoreCompanies, useRestoreCompany } from '../../models/companies/mutations';
import { useTrashList } from '../../models/companies/queries';
import { useBulkRestoreDeals, useRestoreDeal } from '../../models/deals/mutations';
import { useDealTrash } from '../../models/deals/queries';
import { useRestorePipeline } from '../../models/pipelines/mutations';
import { usePipelineTrash } from '../../models/pipelines/queries';

import EntityTrashTab from './EntityTrashTab';
import { COMPANY_COLUMNS, DEAL_COLUMNS, PIPELINE_COLUMNS } from './trashColumns';
import TrashTable, { TRASH_PAGE_SIZE, useRestoreHandler } from './TrashTable';

const TABS = [
	{ key: 'companies', label: 'Unternehmen', icon: BsBuilding },
	{ key: 'deals', label: 'Deals', icon: BsCurrencyEuro },
	{ key: 'pipelines', label: 'Pipelines', icon: BsKanban },
];

const COMPANIES_TAB = {
	namespace: 'companies',
	useList: useTrashList,
	rowsKey: 'companies',
	columns: COMPANY_COLUMNS,
	useRestore: useRestoreCompany,
	useBulkRestore: useBulkRestoreCompanies,
	restoreMessage: (c) => `${c.companyName} wurde wiederhergestellt.`,
	labelSingular: 'Unternehmen',
	labelPlural: 'Unternehmen',
	labelGender: 'n',
	searchPlaceholder: 'Gelöschte Unternehmen durchsuchen …',
	restoreHint:
		'Zusammen mit einem Unternehmen gelöschte Deals, Kontakte und Aktivitäten werden mit wiederhergestellt.',
};

const DEALS_TAB = {
	namespace: 'deals',
	useList: useDealTrash,
	rowsKey: 'deals',
	columns: DEAL_COLUMNS,
	useRestore: useRestoreDeal,
	useBulkRestore: useBulkRestoreDeals,
	restoreMessage: (d) => `Deal "${d.title}" wurde wiederhergestellt.`,
	labelSingular: 'Deal',
	labelPlural: 'Deals',
	labelGender: 'm',
	searchPlaceholder: 'Gelöschte Deals durchsuchen …',
	restoreHint: 'Zusammen mit einem Deal gelöschte Aktivitäten werden mit wiederhergestellt.',
};

function PipelinesTab({ page, onPageChange }) {
	const { data, isLoading } = usePipelineTrash({ page, pageSize: TRASH_PAGE_SIZE });
	const restoreMutation = useRestorePipeline();
	const totalPages = data?.totalPages ?? 1;

	useClampPage({ page, totalPages, setPage: onPageChange, isReady: Boolean(data) });

	const handleRestore = useRestoreHandler(restoreMutation, (p) => `Pipeline "${p.name}" wurde wiederhergestellt.`);

	return (
		<TrashTable
			rows={data?.pipelines ?? []}
			columns={PIPELINE_COLUMNS}
			isLoading={isLoading}
			page={page}
			totalPages={totalPages}
			total={data?.totalCount}
			onPageChange={onPageChange}
			onRestore={handleRestore}
			isRestoring={restoreMutation.isPending}
		/>
	);
}

PipelinesTab.propTypes = { page: PropTypes.number.isRequired, onPageChange: PropTypes.func.isRequired };

const CrmTrashPage = () => {
	const [activeTab, setActiveTab] = useState('companies');
	const [pipelinesPage, setPipelinesPage] = useState(1);

	const { data: companyData } = useTrashList({ page: 1, pageSize: 1 });
	const { data: dealData } = useDealTrash({ page: 1, pageSize: 1 });
	const { data: pipelineData } = usePipelineTrash({ page: 1, pageSize: 1 });

	const counts = {
		companies: companyData?.total ?? 0,
		deals: dealData?.total ?? 0,
		pipelines: pipelineData?.totalCount ?? 0,
	};

	return (
		<Page
			width="full"
			scrollMode="content"
			header={<PageHeader breadcrumbs={[{ label: 'CRM', to: '/crm' }, 'Papierkorb']} />}
		>
			<div className="flex-1 min-h-0 flex flex-col">
				<div className="mb-5">
					<h1 className="text-lg font-bold text-text">Papierkorb</h1>
					<p className="text-sm text-bgBase-800 mt-1">
						Gelöschte Elemente können innerhalb von 30 Tagen wiederhergestellt werden. Danach werden sie
						permanent entfernt.
					</p>
				</div>

				<Tabs
					tabs={TABS.map(({ key, label, icon }) => ({ id: key, label, icon, count: counts[key] }))}
					activeId={activeTab}
					onChange={setActiveTab}
					className="mb-4"
				/>

				<div className="flex-1 min-h-0 flex flex-col">
					{activeTab === 'companies' && <EntityTrashTab key="companies" config={COMPANIES_TAB} />}
					{activeTab === 'deals' && <EntityTrashTab key="deals" config={DEALS_TAB} />}
					{activeTab === 'pipelines' && <PipelinesTab page={pipelinesPage} onPageChange={setPipelinesPage} />}
				</div>
			</div>
		</Page>
	);
};

export default CrmTrashPage;
